import {FC, useEffect, useState} from 'react'
import {i18n} from '../../translate/i18n'
import {secondsToHour} from '../../utils/formater'
import {Hash, Clock, Users, User, Phone} from 'lucide-react'

type QueueType = {
  id: string
  position: number
  minutesOnQueue: string
  people: number
  name: string
  phone: string
  seconds: number
  createdAt: Date
  preferential: boolean
  finalized: 'confirmed' | 'removed' | null
  entryTime: string | null
  updatedAt: string | null
}

type Props = {
  queue: QueueType
  setQueueSelected: (value: any) => void
  setOption: (value: 'removed' | 'confirmed' | 'add' | 'edit') => void
  showWrapperModal: (value: string) => void
  limitMinutes: number
  onProposeAllocation?: (queue: QueueType) => void
}

const QueueCard: FC<Props> = ({
  queue,
  setQueueSelected,
  setOption,
  showWrapperModal,
  limitMinutes,
  onProposeAllocation,
}) => {
  const [newTime, setNewTime] = useState(queue.seconds)

  useEffect(() => {
    const intervalId = setInterval(() => {
      const now = new Date().getTime() / 1000
      const queueDate = new Date(queue.createdAt).getTime() / 1000
      setNewTime(Math.trunc(now - queueDate)) // Atualize o tempo com base no valor anterior
    }, 500)

    // Limpar o intervalo quando o componente for desmontado
    return () => clearInterval(intervalId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div
      className='d-flex flex-row px-4 py-6 align-items-center'
      style={{border: '1px solid #D3D9E8', borderLeft: '4px solid #D3D9E8', borderRadius: 4}}
    >
      <div className='d-flex flex-column w-50 gap-2'>
        {queue.finalized ? (
          <p style={{color: '#6E7586', margin: 0, marginRight: 8}}>
            <b style={{color: '#2A2D36'}}>{i18n.t(`queue.${queue.finalized}`)}</b>{' '}
            {queue.updatedAt ? new Date(queue.updatedAt).toLocaleString().replace(',', '') : ''}
          </p>
        ) : (
          <p
            style={{
              color: '#6E7586',
              margin: 0,
              marginRight: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Hash size={16} style={{color: '#7E8299'}} />
            <b style={{color: '#2A2D36'}}>{i18n.t(`queue.position`)}</b> {queue.position}
          </p>
        )}
        {!queue.finalized && (
          <p
            style={
              queue.minutesOnQueue.split(':')[0] &&
              queue.minutesOnQueue.split(':')[1] &&
              newTime >= limitMinutes * 60
                ? {color: '#EB5353', margin: 0, display: 'flex', alignItems: 'center', gap: 6}
                : {color: '#6E7586', margin: 0, display: 'flex', alignItems: 'center', gap: 6}
            }
          >
            <Clock size={16} color='#4377FF' />
            <b style={{color: '#2A2D36', marginRight: 8}}>{i18n.t(`queue.time`)}</b>
            {secondsToHour(newTime)}
          </p>
        )}
        <p style={{color: '#6E7586', margin: 0, display: 'flex', alignItems: 'center', gap: 6}}>
          <Users size={16} color='#4377FF' />
          <b style={{color: '#2A2D36'}}>
            {queue.people} {i18n.t(`queue.people`)}
          </b>
        </p>
        <p style={{color: '#6E7586', margin: 0, display: 'flex', alignItems: 'center', gap: 6}}>
          <User size={16} color='#4377FF' />
          <b style={{color: '#2A2D36'}}>{queue.name}</b>
        </p>
        <p style={{color: '#6E7586', margin: 0, display: 'flex', alignItems: 'center', gap: 6}}>
          <Phone size={16} color='#4377FF' />
          {queue.phone}
        </p>
        {queue.preferential && (
          <div
            className='mt-2 p-2 d-flex flex-row justify-content-center'
            style={{
              border: '1px solid #EB5353',
              backgroundColor: '#FFDDDD',
              borderRadius: 8,
              width: 100,
            }}
          >
            <p style={{color: '#EB5353', margin: 0}}>{i18n.t('queue.preferential')}</p>
          </div>
        )}
      </div>
      {!queue.finalized && (
        <div className='d-flex flex-column gap-4 w-50'>
          <button
            onClick={() => onProposeAllocation?.(queue)}
            style={{
              border: '1px solid #4377FF',
              borderRadius: '4px',
              backgroundColor: '#4377FF',
              color: 'white',
              width: 60,
              height: 40,
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              ;(e.currentTarget as HTMLButtonElement).style.backgroundColor = '#3561d9'
              ;(e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.05)'
            }}
            onMouseLeave={(e) => {
              ;(e.currentTarget as HTMLButtonElement).style.backgroundColor = '#4377FF'
              ;(e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'
            }}
            title='Propor Alocação de Mesas'
          >
            <i style={{color: 'white'}} className='fa fa-chair' aria-hidden='true'></i>
          </button>
          <button
            onClick={() => {
              setQueueSelected(queue)
              setOption('confirmed')
              showWrapperModal(i18n.t(`queue.confirm`))
            }}
            style={{
              border: '1px solid #5D8ED8',
              borderRadius: '4px',
              backgroundColor: 'transparent',
              width: 60,
              height: 40,
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              ;(e.currentTarget as HTMLButtonElement).style.backgroundColor = '#E8F0FF'
              ;(e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.05)'
            }}
            onMouseLeave={(e) => {
              ;(e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'
              ;(e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'
            }}
            title='Confirmar Chegada'
          >
            <i style={{color: '#5D8ED8'}} className='fa fa-check' aria-hidden='true'></i>
          </button>

          <button
            onClick={() => {
              setQueueSelected(queue)
              setOption('edit')
              showWrapperModal(i18n.t(`general.edit`))
            }}
            style={{
              border: '1px solid #5D8ED8',
              borderRadius: '4px',
              backgroundColor: 'transparent',
              width: 60,
              height: 40,
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              ;(e.currentTarget as HTMLButtonElement).style.backgroundColor = '#E8F0FF'
              ;(e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.05)'
            }}
            onMouseLeave={(e) => {
              ;(e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'
              ;(e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'
            }}
            title='Editar Fila'
          >
            <i style={{color: '#5D8ED8'}} className='fa fa-pen' aria-hidden='true'></i>
          </button>

          <button
            onClick={() => {
              setQueueSelected(queue)
              setOption('removed')
              showWrapperModal(i18n.t(`queue.cancel`))
            }}
            style={{
              border: '1px solid #EB5353',
              borderRadius: '4px',
              backgroundColor: 'transparent',
              width: 60,
              height: 40,
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              ;(e.currentTarget as HTMLButtonElement).style.backgroundColor = '#FFDDDD'
              ;(e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.05)'
            }}
            onMouseLeave={(e) => {
              ;(e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'
              ;(e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'
            }}
            title='Cancelar Fila'
          >
            <i style={{color: '#EB5353'}} className='fa fa-times' aria-hidden='true'></i>
          </button>
        </div>
      )}
    </div>
  )
}

export {QueueCard}
